import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "categoryBody", "categoryTotal",
    "catSubBidTotal", "catContrEstTotal", "catTotalTotal", "catSfCostsTotal",
    "grandTotalBeforeFees", "contractorFeeInput", "grandTotalGmp",
    "feePercentage", "summaryAbove", "summaryBelow", "sfSummaryAbove", "sfSummaryBelow",
    "overallSfCostsTotal",
    "subBidInput", "contrEstInput", "subcontractorsCell", "workCell", "csiCell", "typeCell",
    "addLineItemModal", "addCategoryModal", "newCategoryName",
    "editCategoryModal", "editCategoryName", "approveModal", "unapproveModal", "deleteModal",
    "deleteSovModal", "sovLockedModal",
    "rejectModal", "rejectionComment",
    "constructionAreaModal", "constructionAreaInput",
    "retainageModal", "retainageInput", "retainageDisplay",
    "costCheckerToggle", "costCheckerToggleLabel", "costCheckerBox",
    "requestApprovalModal",
    "newDescription", "newCsiDiv", "newType", "newDefinition",
    "squareFootageDisplay", "gcGrandTotal"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    canEdit: Boolean,
    isOwner: Boolean,
    isContractor: Boolean,
    approved: Boolean,
    squareFootage: Number
  }

  connect() {
    this._pendingDeleteId = null
    this._pendingCategoryId = null
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._showPendingFlash()
  }

  // ── Number/currency helpers ───────────────────────────────────────

  _parseNumber(str) {
    if (!str) return 0
    let cleaned = str.toString().trim()
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned)
    return isNaN(val) ? 0 : val
  }

  _formatNumber(num, precision = 2) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    if (this.currencyFormatValue === "european_currency_format") {
      return abs.toFixed(precision).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      return abs.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      return abs.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
  }

  _formatCurrency(num) {
    const formatted = this._formatNumber(num)
    return formatted ? `${this.currencyValue}${formatted}` : ""
  }

  // ── Focus tracking (store original value) ────────────────────────

  trackFocus(event) {
    const el = event.target
    el.dataset.originalValue = el.value ?? ""
  }

  trackFocusContentEditable(event) {
    const el = event.target
    el.dataset.originalValue = el.textContent.trim()
  }

  // ── Inline editing ────────────────────────────────────────────────

  updateSubBid(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const val = this._parseNumber(input.value)
    input.value = val > 0 ? this._formatNumber(val) : ""
    this._recalcRow(input.closest("tr"))
    this._recalcTotals()
    if (input.value === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, sub_bid: val })
      .then(() => this._showToast("Sub bid updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateContrEst(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const val = this._parseNumber(input.value)
    input.value = val > 0 ? this._formatNumber(val) : ""
    this._recalcRow(input.closest("tr"))
    this._recalcTotals()
    if (input.value === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, contr_est: val })
      .then(() => this._showToast("Contr est updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateSubcontractors(event) {
    const el = event.target
    const id = el.dataset.id
    const subcontractors = el.textContent.trim()
    if (subcontractors === (el.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, subcontractors })
      .then(() => this._showToast("Subcontractors updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateDescription(event) {
    const el = event.target
    const id = el.dataset.id
    const description = el.textContent.trim()
    if (description === (el.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, description })
      .then(() => this._showToast("Description updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateCsiDiv(event) {
    const el = event.target
    const id = el.dataset.id
    const csi_div = el.textContent.trim()
    if (csi_div === (el.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, csi_div })
      .then(() => this._showToast("CSI div updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateType(event) {
    const el = event.target
    const id = el.dataset.id
    const line_item_type = el.textContent.trim()
    if (line_item_type === (el.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, line_item_type })
      .then(() => this._showToast("Type updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  // ── GC SOV inline editing (Qty · Unit · Unit Price → Scheduled Value) ──

  updateQuantity(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const val = this._parseNumber(input.value)
    this._syncSchedFromQtyPrice(input.closest("tr"))
    this._recalcGc()
    if (input.value.trim() === (input.dataset.originalValue ?? "").trim()) return
    this._ajax("PUT", "update_line_item", { id, quantity: val })
      .then(() => this._showToast("Quantity updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateUnitPrice(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const val = this._parseNumber(input.value)
    input.value = val > 0 ? this._formatNumber(val) : ""
    this._syncSchedFromQtyPrice(input.closest("tr"))
    this._recalcGc()
    if (input.value === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, unit_price: val })
      .then(() => this._showToast("Unit price updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  updateUnit(event) {
    const input = event.target
    const id = input.dataset.id
    const unit = input.value.trim()
    if (unit === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, unit })
      .then(() => this._showToast("Unit updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  // User typed a Scheduled Value directly: back-calc Unit Price from Quantity
  // (lump sum when there's no quantity), persist, and re-total.
  updateScheduled(event) {
    const input = event.target
    if (input.readOnly) return
    const id = input.dataset.id
    const val = this._parseNumber(input.value)
    input.value = val > 0 ? this._formatNumber(val) : ""
    const row = input.closest("tr")
    const qty = this._parseNumber(row.querySelector("[data-field='quantity']")?.value || "")
    if (qty > 0) {
      const upEl = row.querySelector("[data-field='unit_price']")
      if (upEl) upEl.value = val > 0 ? this._formatNumber(val / qty) : ""
    }
    this._recalcGc()
    if (input.value === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_line_item", { id, scheduled_value: val })
      .then(() => this._showToast("Total value updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  // Qty/Unit Price changed → refresh that row's Scheduled Value input.
  _syncSchedFromQtyPrice(row) {
    if (!row) return
    const q = this._parseNumber(row.querySelector("[data-field='quantity']")?.value || "")
    const up = this._parseNumber(row.querySelector("[data-field='unit_price']")?.value || "")
    const schedEl = row.querySelector("[data-field='scheduled']")
    if (schedEl && q > 0 && up > 0) schedEl.value = this._formatNumber(q * up)
  }

  _recalcGc() {
    let grand = 0
    this.categoryBodyTargets.forEach(body => {
      let catTotal = 0
      body.querySelectorAll("tr[data-id]").forEach(row => {
        catTotal += this._parseNumber(row.querySelector("[data-field='scheduled']")?.value || "") || 0
      })
      grand += catTotal
      const catCell = body.querySelector(".js-gc-cat-total")
      if (catCell) catCell.textContent = catTotal > 0 ? this._formatNumber(catTotal) : ""
    })
    if (this.hasGcGrandTotalTarget) this.gcGrandTotalTarget.textContent = this._formatNumber(grand)
  }

  updateOwnerApvd(event) {
    const id = event.target.dataset.id
    const ownerApvd = event.target.checked
    this._ajax("PUT", "update_line_item", { id, owner_apvd: ownerApvd })
      .then(() => this._showToast(ownerApvd ? "Line item approved" : "Approval removed"))
      .catch(() => this._showToast("Failed to update approval", "error"))

    // Toggle row editability
    const row = event.target.closest("tr")
    this._toggleRowEditability(row, ownerApvd)
  }

  updateContractorFee(event) {
    const input = event.target
    const val = this._parseNumber(input.value)
    input.value = val > 0 ? this._formatNumber(val) : ""
    this._recalcTotals()
    if (input.value === (input.dataset.originalValue ?? "")) return
    this._ajax("PUT", "update_contractor_fee", { contractor_fee: val })
      .then(() => this._showToast("Contractor fee updated"))
      .catch(() => this._showToast("Failed to update", "error"))
  }

  _toggleRowEditability(row, locked) {
    const subBid = row.querySelector("[data-sov-target='subBidInput']")
    const contrEst = row.querySelector("[data-sov-target='contrEstInput']")
    const subCell = row.querySelector("[data-sov-target='subcontractorsCell']")
    const workCell = row.querySelector("[data-sov-target='workCell']")
    const csiCell = row.querySelector("[data-sov-target='csiCell']")
    const typeCell = row.querySelector("[data-sov-target='typeCell']")

    if (subBid) {
      subBid.readOnly = locked
      subBid.closest("td").classList.toggle("bg-amber-50", !locked)
    }
    if (contrEst) {
      contrEst.readOnly = locked
      contrEst.closest("td").classList.toggle("bg-amber-50", !locked)
    }
    if (subCell) {
      subCell.contentEditable = !locked && this.canEditValue
      subCell.classList.toggle("bg-amber-50", !locked && this.canEditValue)
      subCell.classList.toggle("cursor-text", !locked && this.canEditValue)
    }
    if (workCell) {
      workCell.contentEditable = !locked && this.canEditValue
      workCell.classList.toggle("bg-amber-50", !locked && this.canEditValue)
      workCell.classList.toggle("cursor-text", !locked && this.canEditValue)
    }
    if (csiCell) {
      csiCell.contentEditable = !locked && this.canEditValue
      csiCell.classList.toggle("bg-amber-50", !locked && this.canEditValue)
      csiCell.classList.toggle("cursor-text", !locked && this.canEditValue)
    }
    if (typeCell) {
      typeCell.contentEditable = !locked && this.canEditValue
      typeCell.classList.toggle("bg-amber-50", !locked && this.canEditValue)
      typeCell.classList.toggle("cursor-text", !locked && this.canEditValue)
    }
  }

  // ── Construction Area ─────────────────────────────────────────────

  showConstructionAreaModal() { this.constructionAreaModalTarget.classList.remove("hidden") }
  hideConstructionAreaModal() { this.constructionAreaModalTarget.classList.add("hidden") }

  // ── Retainage (builder SOV page — same project setting the owner edits) ──

  showRetainageModal() {
    this.retainageModalTarget.classList.remove("hidden")
    if (this.hasRetainageInputTarget) this.retainageInputTarget.focus()
  }

  hideRetainageModal() { this.retainageModalTarget.classList.add("hidden") }

  async submitRetainage() {
    const val = parseFloat(this.retainageInputTarget.value)
    if (Number.isNaN(val) || val < 0 || val > 100) {
      this._showToast("Enter a percentage between 0 and 100", "error")
      return
    }
    const ok = await this._ajax("PUT", "update_retainage", { retainage: val })
    this.hideRetainageModal()
    if (ok) {
      if (this.hasRetainageDisplayTarget) {
        this.retainageDisplayTarget.textContent = `${val % 1 === 0 ? val.toFixed(0) : val}%`
      }
      this._showToast("Retainage updated")
    } else {
      this._showToast("Failed to update retainage", "error")
    }
  }

  async updateConstructionArea() {
    const val = parseFloat(this.constructionAreaInputTarget.value) || 0
    await this._ajax("PUT", "update_construction_area", { construction_area: val })
    this.squareFootageValue = val
    if (this.hasSquareFootageDisplayTarget) {
      this.squareFootageDisplayTarget.textContent = val % 1 === 0 ? val.toFixed(0) : val
    }
    this.hideConstructionAreaModal()
    this._recalcTotals()
    this._showToast("Construction area updated", "success")
  }

  // ── Add line item ─────────────────────────────────────────────────

  showAddLineItemModal(event) {
    event.preventDefault()
    this._pendingCategoryId = event.currentTarget.dataset.categoryId
    this.newDescriptionTarget.value = ""
    if (this.hasNewCsiDivTarget) this.newCsiDivTarget.value = ""
    if (this.hasNewTypeTarget) this.newTypeTarget.value = ""
    if (this.hasNewDefinitionTarget) this.newDefinitionTarget.value = ""
    this.addLineItemModalTarget.classList.remove("hidden")
  }

  hideAddLineItemModal() { this.addLineItemModalTarget.classList.add("hidden") }

  // ── Add category (Builder persona) ────────────────────────────────

  showAddCategoryModal(event) {
    event?.preventDefault()
    if (this.hasNewCategoryNameTarget) this.newCategoryNameTarget.value = ""
    this.addCategoryModalTarget.classList.remove("hidden")
    if (this.hasNewCategoryNameTarget) this.newCategoryNameTarget.focus()
  }

  hideAddCategoryModal() { this.addCategoryModalTarget.classList.add("hidden") }

  async submitAddCategory() {
    const name = this.hasNewCategoryNameTarget ? this.newCategoryNameTarget.value.trim() : ""
    if (!name) return

    const kind = this.element.querySelector('input[name="new_cat_kind"]:checked')?.value || "line_item"
    const resp = await this._ajax("POST", "create_project_sov_category", { name, kind })
    if (resp?.id) {
      this._flashAndReload("Category added") // new category renders with its own + Add Line Item
    } else {
      this._showToast("Failed to add category", "error")
    }
  }

  // ── Edit category — rename or delete (Builder persona) ─────────────

  showEditCategoryModal(event) {
    event.preventDefault()
    this._editCategoryId = event.currentTarget.dataset.categoryId
    if (this.hasEditCategoryNameTarget) {
      this.editCategoryNameTarget.value = event.currentTarget.dataset.categoryName || ""
    }
    this.editCategoryModalTarget.classList.remove("hidden")
    if (this.hasEditCategoryNameTarget) this.editCategoryNameTarget.focus()
  }

  hideEditCategoryModal() { this.editCategoryModalTarget.classList.add("hidden") }

  async submitRenameCategory() {
    const name = this.hasEditCategoryNameTarget ? this.editCategoryNameTarget.value.trim() : ""
    if (!name) return

    const ok = await this._ajax("PATCH", "rename_project_sov_category", { id: this._editCategoryId, name })
    this.hideEditCategoryModal()
    if (ok) {
      this._flashAndReload("Category renamed")
    } else {
      this._showToast("Failed to rename category", "error")
    }
  }

  async deleteCategoryFromModal() {
    const ok = await this._ajax("DELETE", "delete_project_sov_category", { id: this._editCategoryId })
    this.hideEditCategoryModal()
    if (ok) {
      this._flashAndReload("Category removed")
    } else {
      this._showToast("Failed to remove category", "error")
    }
  }

  stopPropagation(event) { event.stopPropagation() }

  // ── AI Cost Checker on/off (admins only — persists per user) ──

  // From the "Checker: on/off" button, or from the AI Cost Checker box in
  // the grid's Columns gear (Phelipe 2026-10-23); the two stay in step.
  async toggleCostChecker(event) {
    const btn = this.costCheckerToggleTarget
    const fromBox = event?.target?.type === "checkbox"
    const next = fromBox ? event.target.checked : btn.dataset.enabled !== "true"
    btn.dataset.enabled = String(next)
    if (this.hasCostCheckerToggleLabelTarget) {
      this.costCheckerToggleLabelTarget.textContent = next ? "Checker: on" : "Checker: off"
    }
    if (this.hasCostCheckerBoxTarget) this.costCheckerBoxTarget.checked = next
    // Live-hide the checker column + run button; server render follows the
    // saved setting on the next load.
    document.querySelectorAll(".js-benchmark-col").forEach(el => { el.style.display = next ? "" : "none" })
    document.querySelectorAll(".js-cost-checker-btn").forEach(el => { el.style.display = next ? "" : "none" })

    try {
      await fetch(btn.dataset.settingsUrl, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken, "Accept": "application/json" },
        body: JSON.stringify({ setting: { sov_cost_checker_enabled: next } })
      })
      // Turning ON when the column was never rendered needs a fresh render.
      if (next && !document.querySelector(".js-benchmark-col")) {
        this._flashAndReload("AI Cost Checker on")
        return
      }
      this._showToast(next ? "AI Cost Checker on" : "AI Cost Checker off")
    } catch (e) {
      this._showToast("Couldn't save the setting", "error")
    }
  }

  // ── Delete entire SOV (kebab menu) ──

  showDeleteSovModal(event) {
    event?.preventDefault()
    // Locked (Phelipe 2026-10-23): say so instead of offering the delete.
    if (event?.params?.locked && this.hasSovLockedModalTarget) {
      this.sovLockedModalTarget.classList.remove("hidden")
      return
    }
    this.deleteSovModalTarget.classList.remove("hidden")
  }

  hideSovLockedModal() { if (this.hasSovLockedModalTarget) this.sovLockedModalTarget.classList.add("hidden") }

  hideDeleteSovModal() { this.deleteSovModalTarget.classList.add("hidden") }

  async confirmDeleteSov() {
    const ok = await this._ajax("DELETE", "delete_project_sov", {})
    this.hideDeleteSovModal()
    if (ok) {
      this._flashAndReload("Schedule of Values deleted")
    } else {
      this._showToast("Failed to delete the SOV", "error")
    }
  }

  async submitAddLineItem() {
    const description = this.newDescriptionTarget.value.trim()
    if (!description) return

    const csiDiv = this.hasNewCsiDivTarget ? this.newCsiDivTarget.value.trim() : ""
    const type = this.hasNewTypeTarget ? this.newTypeTarget.value.trim() : ""
    const definition = this.hasNewDefinitionTarget ? this.newDefinitionTarget.value.trim() : ""

    const resp = await this._ajax("POST", "add_custom_sov_line_item", {
      sov_category_id: this._pendingCategoryId,
      description,
      csi_div: csiDiv,
      line_item_type: type,
      definition
    })
    if (resp?.new_li_id) {
      this.hideAddLineItemModal()
      if (this.hasGcGrandTotalTarget) {
        this._flashAndReload("Line item added") // GC table renders its own row shape
        return
      }
      this._insertLineItemRow(resp.new_li_id, description, csiDiv, type)
      this._recalcTotals()
      this._showToast("Line item added")
    } else {
      this._showToast("Failed to add line item", "error")
    }
  }

  // ── Remove line item ──────────────────────────────────────────────

  removeLineItem(event) {
    event.preventDefault()
    this._pendingDeleteId = event.currentTarget.dataset.id
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    await this._ajax("DELETE", "delete_custom_sov_line_item", { line_item_id: this._pendingDeleteId })
    this.hideDeleteModal()

    const row = this.element.querySelector(`tr[data-id="${this._pendingDeleteId}"]`)
    if (row) row.remove()
    if (this.hasGcGrandTotalTarget) { this._recalcGc() } else { this._recalcTotals() }
    this._showToast("Line item deleted")
  }

  // ── Approve / Unapprove / Reject ──────────────────────────────────

  showApproveModal() { this.approveModalTarget.classList.remove("hidden") }
  hideApproveModal() { this.approveModalTarget.classList.add("hidden") }
  showUnapproveModal() { this.unapproveModalTarget.classList.remove("hidden") }
  hideUnapproveModal() { this.unapproveModalTarget.classList.add("hidden") }

  async confirmApprove() {
    const resp = await this._ajax("PUT", "approve_sov", { sign: "yes" })
    this.hideApproveModal()
    if (resp?.approved) {
      this._flashAndReload("Schedule of Values approved")
    }
  }

  async confirmUnapprove() {
    const resp = await this._ajax("PUT", "approve_sov", { sign: "no" })
    this.hideUnapproveModal()
    if (resp && !resp.approved) {
      this._flashAndReload("Schedule of Values unapproved")
    }
  }

  // Reject
  showRejectModal() { this.rejectModalTarget.classList.remove("hidden") }
  hideRejectModal() { this.rejectModalTarget.classList.add("hidden") }

  async submitReject() {
    const comment = this.rejectionCommentTarget.value.trim()
    if (!comment) {
      this._showToast("Please enter a rejection comment", "error")
      return
    }
    await this._ajax("POST", "reject_sov", { rejection_comment: comment })
    this.hideRejectModal()
    this._flashAndReload("Schedule of Values rejected")
  }

  // ── Request approval ──────────────────────────────────────────────

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() { this.requestApprovalModalTarget.classList.add("hidden") }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    await this._ajax("POST", "request_approval", { emails: JSON.stringify(emails) })
    this.hideRequestApprovalModal()
    this._flashAndReload("Approval request sent")
  }

  // ── Recalculate totals ────────────────────────────────────────────

  _recalcRow(row) {
    if (!row) return
    const subBid = this._parseNumber(row.querySelector("[data-sov-target='subBidInput']")?.value)
    const contrEst = this._parseNumber(row.querySelector("[data-sov-target='contrEstInput']")?.value)
    const total = subBid + contrEst
    const totalCell = row.querySelector(".js-line-total")
    if (totalCell) {
      totalCell.textContent = total > 0 ? this._formatNumber(total) : ""
      this._toggleSym(totalCell, total > 0)
    }

    const sfCell = row.querySelector(".js-line-sf")
    const sf = this.squareFootageValue
    if (sfCell) {
      sfCell.textContent = (sf > 0 && total > 0) ? this._formatNumber(total / sf, 3) : ""
    }
  }

  _toggleSym(el, show) {
    const sym = el?.previousElementSibling
    if (sym) sym.textContent = show ? this.currencyValue : ""
  }

  _recalcTotals() {
    let grandSubBid = 0
    let grandContrEst = 0
    let grandTotal = 0

    // Above slab = items 21-66, Below slab = items 1-20 and 67-70
    let aboveSlab = 0
    let belowSlab = 0

    this.categoryBodyTargets.forEach((tbody, i) => {
      let catSubBid = 0
      let catContrEst = 0

      tbody.querySelectorAll("tr[data-id]").forEach(row => {
        const sb = this._parseNumber(row.querySelector("[data-sov-target='subBidInput']")?.value)
        const ce = this._parseNumber(row.querySelector("[data-sov-target='contrEstInput']")?.value)
        const lineTotal = sb + ce
        catSubBid += sb
        catContrEst += ce

        // Update row total and SF
        const totalCell = row.querySelector(".js-line-total")
        if (totalCell) {
          totalCell.textContent = lineTotal > 0 ? this._formatNumber(lineTotal) : ""
          this._toggleSym(totalCell, lineTotal > 0)
        }

        const sfCell = row.querySelector(".js-line-sf")
        if (sfCell) {
          sfCell.textContent = (this.squareFootageValue > 0 && lineTotal > 0) ? this._formatNumber(lineTotal / this.squareFootageValue, 3) : ""
        }

        // Summary of costs
        const itemNum = parseInt(row.dataset.itemNum) || 0
        if (itemNum >= 21 && itemNum <= 66) {
          aboveSlab += lineTotal
        } else if ((itemNum >= 1 && itemNum <= 20) || (itemNum >= 67 && itemNum <= 70)) {
          belowSlab += lineTotal
        }
      })

      const catTotal = catSubBid + catContrEst
      grandSubBid += catSubBid
      grandContrEst += catContrEst
      grandTotal += catTotal

      // Update category totals
      if (this.catSubBidTotalTargets[i]) {
        this.catSubBidTotalTargets[i].textContent = catSubBid > 0 ? this._formatNumber(catSubBid) : ""
        this._toggleSym(this.catSubBidTotalTargets[i], catSubBid > 0)
      }
      if (this.catContrEstTotalTargets[i]) {
        this.catContrEstTotalTargets[i].textContent = catContrEst > 0 ? this._formatNumber(catContrEst) : ""
        this._toggleSym(this.catContrEstTotalTargets[i], catContrEst > 0)
      }
      if (this.catTotalTotalTargets[i]) {
        this.catTotalTotalTargets[i].textContent = catTotal > 0 ? this._formatNumber(catTotal) : ""
        this._toggleSym(this.catTotalTotalTargets[i], catTotal > 0)
      }
      if (this.catSfCostsTotalTargets[i]) {
        this.catSfCostsTotalTargets[i].textContent = (this.squareFootageValue > 0 && catTotal > 0) ? this._formatNumber(catTotal / this.squareFootageValue, 3) : ""
      }
    })

    // Grand total before fees
    if (this.hasGrandTotalBeforeFeesTarget) {
      this.grandTotalBeforeFeesTarget.textContent = grandTotal > 0 ? this._formatNumber(grandTotal) : ""
      this._toggleSym(this.grandTotalBeforeFeesTarget, grandTotal > 0)
    }

    // Contractor fee and percentage
    const contractorFee = this._parseNumber(this.hasContractorFeeInputTarget ? this.contractorFeeInputTarget.value : "0")
    if (this.hasFeePercentageTarget) {
      if (grandTotal > 0 && contractorFee > 0) {
        this.feePercentageTarget.textContent = ((contractorFee / grandTotal) * 100).toFixed(2) + "%"
      } else {
        this.feePercentageTarget.textContent = ""
      }
    }

    // GMP
    const gmp = grandTotal + contractorFee
    if (this.hasGrandTotalGmpTarget) {
      this.grandTotalGmpTarget.textContent = gmp > 0 ? this._formatNumber(gmp) : ""
      this._toggleSym(this.grandTotalGmpTarget, gmp > 0)
    }

    // Summary of costs
    const aboveWithFee = aboveSlab + contractorFee
    if (this.hasSummaryAboveTarget) {
      this.summaryAboveTarget.textContent = aboveWithFee > 0 ? this._formatNumber(aboveWithFee) : ""
      this._toggleSym(this.summaryAboveTarget, aboveWithFee > 0)
    }
    if (this.hasSummaryBelowTarget) {
      this.summaryBelowTarget.textContent = belowSlab > 0 ? this._formatNumber(belowSlab) : ""
      this._toggleSym(this.summaryBelowTarget, belowSlab > 0)
    }

    // SF summary
    if (this.squareFootageValue > 0) {
      if (this.hasSfSummaryAboveTarget) this.sfSummaryAboveTarget.textContent = aboveWithFee > 0 ? this._formatNumber(aboveWithFee / this.squareFootageValue, 3) : ""
      if (this.hasSfSummaryBelowTarget) this.sfSummaryBelowTarget.textContent = belowSlab > 0 ? this._formatNumber(belowSlab / this.squareFootageValue, 3) : ""
    }

    // Overall SF costs total
    if (this.hasOverallSfCostsTotalTarget && this.squareFootageValue > 0) {
      this.overallSfCostsTotalTarget.textContent = grandTotal > 0 ? this._formatNumber(grandTotal / this.squareFootageValue, 3) : ""
    }
  }

  // ── Insert line item row into DOM ────────────────────────────────

  _insertLineItemRow(id, description, csiDiv, type) {
    const tbody = this.categoryBodyTargets.find(
      tb => tb.dataset.categoryId === String(this._pendingCategoryId)
    )
    if (!tbody) return

    const canEdit = this.canEditValue
    const isOwner = this.isOwnerValue
    const currency = this.currencyValue

    const trashSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/></svg>'

    const tr = document.createElement("tr")
    tr.className = "bg-white border-b border-l border-r border-gray-200 group"
    tr.dataset.id = id

    // Item (empty for custom)
    let td = document.createElement("td")
    td.className = "px-2 py-1 border-r border-gray-200 text-gray-500 text-center"
    td.innerHTML = `<button type="button" class="p-0.5 text-gray-400 hover:text-red-500 transition-colors cursor-pointer" data-action="click->sov#removeLineItem" data-id="${id}" title="Delete">${trashSvg}</button>`
    tr.appendChild(td)

    // CSI Div
    td = document.createElement("td")
    td.className = `px-2 py-1 border-r border-gray-200 ${canEdit ? 'bg-amber-50 cursor-text' : ''}`
    td.contentEditable = canEdit
    td.dataset.action = "blur->sov#updateCsiDiv"
    td.dataset.sovTarget = "csiCell"
    td.dataset.id = id
    td.textContent = csiDiv
    tr.appendChild(td)

    // Type
    td = document.createElement("td")
    td.className = `px-2 py-1 border-r border-gray-200 ${canEdit ? 'bg-amber-50 cursor-text' : ''}`
    td.contentEditable = canEdit
    td.dataset.action = "blur->sov#updateType"
    td.dataset.sovTarget = "typeCell"
    td.dataset.id = id
    td.textContent = type
    tr.appendChild(td)

    // Description
    td = document.createElement("td")
    td.className = `px-2 py-1 border-r border-gray-200 ${canEdit ? 'bg-amber-50 cursor-text' : ''}`
    td.contentEditable = canEdit
    td.dataset.action = "blur->sov#updateDescription"
    td.dataset.sovTarget = "workCell"
    td.dataset.id = id
    td.textContent = description
    tr.appendChild(td)

    // Sub Bid (two-cell pattern)
    td = document.createElement("td")
    td.className = "px-0 py-0 border-r border-gray-200 bg-amber-50"
    td.innerHTML = `<div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[13px]">${currency}</span><input type="text" inputmode="decimal" class="w-full px-1 py-1 pr-2 text-right text-[13px] bg-transparent focus:outline-none" data-sov-target="subBidInput" data-action="blur->sov#updateSubBid" data-id="${id}" value=""></div>`
    tr.appendChild(td)

    // Contr Est
    td = document.createElement("td")
    td.className = "px-0 py-0 border-r border-gray-200 bg-amber-50"
    td.innerHTML = `<div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[13px]">${currency}</span><input type="text" inputmode="decimal" class="w-full px-1 py-1 pr-2 text-right text-[13px] bg-transparent focus:outline-none" data-sov-target="contrEstInput" data-action="blur->sov#updateContrEst" data-id="${id}" value=""></div>`
    tr.appendChild(td)

    // Total
    td = document.createElement("td")
    td.className = "px-2 py-1 border-r border-gray-200 text-right"
    td.innerHTML = `<div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[13px]">${currency}</span><span class="flex-1 pr-2 text-right js-line-total"></span></div>`
    tr.appendChild(td)

    // SF Costs
    td = document.createElement("td")
    td.className = "px-2 py-1 border-r border-gray-200 text-right js-line-sf"
    tr.appendChild(td)

    // Subcontractors
    td = document.createElement("td")
    td.className = `px-2 py-1 border-r border-gray-200 ${canEdit ? 'bg-amber-50 cursor-text' : ''}`
    td.contentEditable = canEdit
    td.dataset.action = "blur->sov#updateSubcontractors"
    td.dataset.sovTarget = "subcontractorsCell"
    td.dataset.id = id
    tr.appendChild(td)

    // Owner Apvd
    td = document.createElement("td")
    td.className = "px-2 py-1"
    td.innerHTML = `<div class="flex items-center justify-center"><input type="checkbox" data-action="change->sov#updateOwnerApvd" data-id="${id}" class="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand" ${isOwner ? '' : 'disabled'}></div>`
    tr.appendChild(td)

    // Insert before the totals row
    const totalsRow = tbody.querySelector("tr.font-semibold")
    tbody.insertBefore(tr, totalsRow)
  }

  // ── Toast helpers ───────────────────────────────────────────────

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("sov_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("sov_flash")
    if (!raw) return
    sessionStorage.removeItem("sov_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("SOV request failed:", e)
    }
  }
}
